import ToolbarButton from "./toolbarButton/toolbarButton.component";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../../api/users";
import { useAuthContext } from "../../../contexts/auth";
import styles from "./toolbar.module.css"
import { Action } from "../../../types/toolbar";
import CustomModal from "../modal/modal.component";
import {
  getToolbarShortcutById,
  TOOLBAR_SHORTCUT_PENDING_KEY,
  TOOLBAR_SHORTCUT_REGISTRY,
} from "./toolbarShortcutRegistry";

const TOOLBAR_SHORTCUT_SETTINGS_KEY = "__toolbar_shortcut_v1";

interface ToolbarShortcutSettings {
  selected_shortcut_id?: string | null;
  version?: number;
}

export interface ToolbarProps {
  actions: Action[];
  noTopPadding?: boolean;
  showShortcut?: boolean;
}

export const Toolbar = ({ actions, noTopPadding = false, showShortcut = true }: ToolbarProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, setCurrentUser } = useAuthContext();
  const { update: updateCurrentUser } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [shortcutModalOpened, setShortcutModalOpened] = useState(false);
  const [selectedShortcutId, setSelectedShortcutId] = useState<string>("");
  const [savingShortcut, setSavingShortcut] = useState(false);

  const availableActionsById = useMemo(
    () =>
      actions.reduce<Record<string, Action>>((acc, action) => {
        if (action.id) {
          acc[action.id] = action;
        }
        return acc;
      }, {}),
    [actions]
  );

  const currentShortcutSettings =
    (currentUser?.datagrid_settings?.[
      TOOLBAR_SHORTCUT_SETTINGS_KEY
    ] as ToolbarShortcutSettings | undefined) ?? {};

  useEffect(() => {
    setSelectedShortcutId(currentShortcutSettings.selected_shortcut_id ?? "");
  }, [currentShortcutSettings.selected_shortcut_id]);

  const selectedShortcutOption = TOOLBAR_SHORTCUT_REGISTRY.find(
    (option) => option.id === selectedShortcutId
  );

  const visibleActions = useMemo(
    () =>
      actions.filter(
        (action) => !(selectedShortcutOption && action.id === selectedShortcutOption.id)
      ),
    [actions, selectedShortcutOption]
  );

  const shortcutLabel = selectedShortcutOption
    ? t(selectedShortcutOption.labelKey)
    : t("Set shortcut");

  useEffect(() => {
    const pendingShortcutId = window.sessionStorage.getItem(TOOLBAR_SHORTCUT_PENDING_KEY);

    if (!pendingShortcutId) {
      return;
    }

    const pendingShortcutAction = actions.find(
      (action) => action.id === pendingShortcutId
    );

    if (!pendingShortcutAction) {
      return;
    }

    window.sessionStorage.removeItem(TOOLBAR_SHORTCUT_PENDING_KEY);

    if (pendingShortcutAction.disabled) {
      enqueueSnackbar(t("Shortcut unavailable"), { variant: "warning" });
      return;
    }

    pendingShortcutAction.onClick();
  }, [actions, enqueueSnackbar, t]);

  const openShortcutOnRoute = useCallback(
    (shortcutId: string) => {
      const registryItem = getToolbarShortcutById(shortcutId);
      const currentAction = actions.find((action) => action.id === shortcutId);

      if (currentAction && !currentAction.disabled) {
        currentAction.onClick();
        return;
      }

      if (!registryItem) {
        enqueueSnackbar(t("Shortcut unavailable"), { variant: "warning" });
        return;
      }

      window.sessionStorage.setItem(TOOLBAR_SHORTCUT_PENDING_KEY, shortcutId);

      if (location.pathname !== registryItem.targetPath) {
        navigate(registryItem.targetPath);
      }
    },
    [actions, enqueueSnackbar, location.pathname, navigate, t]
  );

  const handleShortcutClick = () => {
    if (!selectedShortcutId) {
      setShortcutModalOpened(true);
      return;
    }

    openShortcutOnRoute(selectedShortcutId);
  };

  const handleSaveShortcut = useCallback(async () => {
    if (!currentUser || savingShortcut) {
      return;
    }

    setSavingShortcut(true);

    const nextDatagridSettings = {
      ...(currentUser.datagrid_settings ?? {}),
      [TOOLBAR_SHORTCUT_SETTINGS_KEY]: {
        selected_shortcut_id: selectedShortcutId || null,
        version: 1,
      },
    };

    const result = await updateCurrentUser({ datagrid_settings: nextDatagridSettings });

    if (result?.success && result?.data) {
      setCurrentUser(result.data);
      enqueueSnackbar(t("Shortcut updated"));
      setShortcutModalOpened(false);
    } else {
      enqueueSnackbar(t("Shortcut update failed"));
    }

    setSavingShortcut(false);
  }, [currentUser, enqueueSnackbar, savingShortcut, selectedShortcutId, setCurrentUser, t, updateCurrentUser]);

  const shortcutDisabled =
    !!selectedShortcutId && !selectedShortcutOption;

  const shortcutModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 12%, transparent), color-mix(in srgb, var(--componentBgColor) 92%, white))",
          border: "1px solid var(--borderColor)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {t("Choose shortcut")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Pick one action that should be available from the bottom shortcut.")}
        </Typography>
      </Box>

      <RadioGroup
        value={selectedShortcutId}
        onChange={(event) => setSelectedShortcutId(event.target.value)}
      >
        <Stack spacing={1.25}>
          {TOOLBAR_SHORTCUT_REGISTRY.map((option) => {
            const availableAction = availableActionsById[option.id];

            return (
              <Box
                key={option.id}
                sx={{
                  borderRadius: 2,
                  border: "1px solid var(--borderColor)",
                  backgroundColor:
                    selectedShortcutId === option.id ? "var(--listItemSelectedBg)" : "var(--componentBgColor)",
                  transition: "transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    borderColor: "var(--primaryColor)",
                  },
                }}
              >
                <FormControlLabel
                  value={option.id}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(option.labelKey)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {availableAction ? t("Available on this page") : t("Will open on its target page")}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: "100%",
                    m: 0,
                    px: 1.5,
                    py: 1,
                    alignItems: "flex-start",
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </RadioGroup>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t("You can change this shortcut anytime.")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="text" onClick={() => setShortcutModalOpened(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveShortcut} disabled={savingShortcut}>
            {savingShortcut ? t("Processing...") : t("Save")}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className={styles.root}>
      <Box className={`${styles.actions} ${noTopPadding ? styles.noTopPadding : ""}`}>
        {visibleActions.map((action, index) => (
          <ToolbarButton
            key={index}
            tourId={action.id}
            icon={action.icon}
            label={action.label}
            onClick={action.onClick}
            color={action.color}
            disabled={action.disabled}
            trailingAdornment={action.trailingAdornment}
          />
        ))}
      </Box>

      {showShortcut && (
        <>
          <Box className={styles.shortcutSection}>
            <ToolbarButton
              icon={<BoltIcon />}
              label={shortcutLabel}
              onClick={handleShortcutClick}
              color={undefined}
              disabled={shortcutDisabled}
            />
            <Button
              variant="text"
              size="small"
              onClick={() => setShortcutModalOpened(true)}
              sx={{ alignSelf: "flex-end", mr: 0.5 }}
            >
              {t("Change shortcut")}
            </Button>
          </Box>

          <CustomModal
            opened={shortcutModalOpened}
            setOpened={setShortcutModalOpened}
            title={t("Toolbar shortcut")}
            content={shortcutModalContent}
            width={420}
          />
        </>
      )}
    </Box>
  );
};
